// src/pages/Dashboard.jsx
//
// App shell: Sidebar + Topbar (src/layout/) wrapping the routed page
// content. Rewritten from the old inline dark-navy pill-nav header/sidebar
// to the approved Jade Orion design — see src/layout/shell.css for the
// stylesheet and src/layout/Sidebar.jsx / Topbar.jsx for the components.
// Permission-based visibility (ctx.hasPermission/anyVisible) and
// autoOpenPaths (expand the group containing the current route) are
// unchanged from before; only the markup/styling moved.
//
// User/permissions/module-subscription state comes from useAuth()/
// useWorkspace() (AuthContext/WorkspaceContext), not utils/session —
// permissions are resolved from GET /auth/me, not decoded from the JWT
// (see AuthContext.jsx's own docstring), and activeModules reflects the
// current project's own account subscription (see WorkspaceContext.jsx).

import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { NAV_SECTIONS } from "./navConfig";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import Sidebar from "../layout/Sidebar";
import Topbar from "../layout/Topbar";
import { SWITCHER_HIDDEN_PATH_PREFIXES } from "../utils/constants";
import "../layout/shell.css";

/** Walks the nav registry collecting every entry's autoOpenPaths result for the current route. */
function collectAutoOpen(entries, pathname, acc = {}) {
  for (const entry of entries) {
    if (entry.autoOpenPaths) {
      acc[entry.key] = entry.autoOpenPaths.some((prefix) => pathname.startsWith(prefix));
    }
    if (entry.items) collectAutoOpen(entry.items, pathname, acc);
  }
  return acc;
}

export default function Dashboard() {
  const location = useLocation();

  const { user, logout, hasPermission, hasAnyPermission, hasPermissionAnywhere } = useAuth();
  const { engagements, activeModules } = useWorkspace();

  const username = user?.username || user?.email || "User";
  const role = (user?.roles || [])[0] || "";

  const ctx = useMemo(
    () => ({
      hasPermission,
      hasAnyPermission,
      hasPermissionAnywhere,
      anyVisible: (items = []) => items.some(Boolean),
      hasModule: (name) => activeModules.has(name),
      engagementsCount: engagements.length,
    }),
    [hasPermission, hasAnyPermission, hasPermissionAnywhere, activeModules, engagements]
  );

  const [collapsed, setCollapsed] = useState(false);
  const [openMap, setOpenMap] = useState({});
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const auto = collectAutoOpen(NAV_SECTIONS, location.pathname || "/");
    setOpenMap((prev) => ({ ...auto, ...prev }));
  }, [location.pathname]);

  const toggleGroup = (key) => setOpenMap((m) => ({ ...m, [key]: !m[key] }));

  const hideSwitchers = SWITCHER_HIDDEN_PATH_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));

  // Topbar depth-on-scroll: only a subtle shadow once the routed content has
  // scrolled under it, so the topbar reads as "above" the page.
  const onContentScroll = (e) => {
    setScrolled(e.currentTarget.scrollTop > 4);
  };

  return (
    <div className="orion-shell">
      <Sidebar
        collapsed={collapsed}
        openMap={openMap}
        onToggleGroup={toggleGroup}
        ctx={ctx}
        user={{ username, role }}
      />

      <div className="orion-main">
        <Topbar
          onToggleSidebar={() => setCollapsed((c) => !c)}
          username={username}
          role={role}
          onLogout={logout}
          scrolled={scrolled}
          hideSwitchers={hideSwitchers}
        />

        <main className="orion-content" onScroll={onContentScroll}>
          <div className="orion-content-inner">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
